import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, AtSignIcon, BarChart3Icon, BotIcon, BriefcaseIcon, Building2Icon, CalendarDaysIcon, CheckIcon, ChevronDownIcon, FilterIcon, LinkIcon, ImageIcon, InstagramIcon, LinkedinIcon, MagnetIcon, MapPinIcon, MegaphoneIcon, MessageCircleIcon, PhoneIcon, SendIcon, SparklesIcon, TargetIcon, TrendingUpIcon, UsersIcon, VideoIcon, YoutubeIcon } from 'lucide-react';
import { Form, LandingBlock, LandingBlockType, LandingPage } from '../../types';
import { blockDefinition } from '../../data/blockLibrary';
import { BrandLogo } from '../shared/BrandLogo';
import { Button } from '../ui/Button';
import { VideoReplay } from './VideoReplay';
import { SOCIAL_NETWORKS } from '../../data/socialNetworks';
import { DynamicForm, DynamicFormResult } from './DynamicForm';
const ICONS: Record<string, React.ComponentType<{
  className?: string;
}>> = {
  Target: TargetIcon,
  Users: UsersIcon,
  Video: VideoIcon,
  Megaphone: MegaphoneIcon,
  Sparkles: SparklesIcon,
  Bot: BotIcon,
  BarChart3: BarChart3Icon,
  Building2: Building2Icon,
  CalendarDays: CalendarDaysIcon,
  MessageCircle: MessageCircleIcon,
  Briefcase: BriefcaseIcon,
  Bot: BotIcon,
  Magnet: MagnetIcon,
  Filter: FilterIcon,
  TrendingUp: TrendingUpIcon
};
interface LandingRendererProps {
  landing: LandingPage;
  form?: Form;
  onSubmit?: (result: DynamicFormResult) => Promise<void>;
  preview?: boolean;
  /** Muestra los accesos de la plataforma en la barra superior (home de AVA). */
  authLinks?: boolean;
  /** Bloque resaltado en el constructor visual. */
  selectedBlockId?: string | null;
  /** Permite seleccionar un bloque haciendo clic sobre él en la vista previa. */
  onSelectBlock?: (blockId: string) => void;
  /**
   * Dispositivo simulado en la vista previa del constructor. Con 'mobile' se
   * aplican los mismos ajustes de móvil que en la landing pública.
   */
  viewport?: 'desktop' | 'tablet' | 'mobile';
}
const SPACING: Record<LandingBlock['spacing'], string> = {
  compacto: 'py-10 sm:py-14',
  normal: 'py-16 sm:py-24',
  amplio: 'py-24 sm:py-32'
};

/**
 * Destino del CTA principal: el formulario multipaso de cualificación. Si la
 * landing no lo incluye, baja al calendario.
 */
function scrollToForm() {
  const target = document.getElementById('formulario') ?? document.getElementById('calendario');
  target?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
}
function cfg<T>(block: LandingBlock, key: string, fallback: T): T {
  const value = block.config[key];
  return (value === undefined || value === null ? fallback : value) as T;
}

/** Interruptores del constructor: guardan «si» / «no» o un booleano. */
function flag(block: LandingBlock, key: string, fallback: boolean): boolean {
  const value = block.config[key];
  if (value === undefined || value === null || value === '') return fallback;
  if (typeof value === 'boolean') return value;
  return String(value) !== 'no';
}
function blockLabel(type: LandingBlockType): string {
  return blockDefinition(type).label;
}
const SHADOWS: Record<string, string> = {
  ninguna: 'none',
  suave: '0 4px 20px -6px rgba(11,31,77,0.10)',
  media: '0 14px 36px -12px rgba(11,31,77,0.20)',
  fuerte: '0 28px 64px -18px rgba(11,31,77,0.30)'
};
const BACKGROUNDS: Record<string, string | undefined> = {
  transparente: undefined,
  blanco: '#FFFFFF',
  gris: '#F5F7FA',
  marca: 'var(--primary)',
  oscuro: '#0B1F4D'
};

/**
 * Ajustes de móvil del bloque: se publican como variables CSS y una clase por
 * propiedad definida, para que `index.css` las aplique solo bajo 640 px. Así la
 * versión móvil se edita sin alterar escritorio ni tablet, y el constructor y
 * la landing pública comparten exactamente las mismas reglas.
 */
function deviceStyle(block: LandingBlock, device: 'm' | 't'): {
  vars: Record<string, string>;
  className: string;
} {
  const s = device === 'm' ? block.style?.mobile : block.style?.tablet;
  if (!s || s.sync !== false) return {
    vars: {},
    className: ''
  };
  const vars: Record<string, string> = {};
  const classes: string[] = [];
  const add = (key: string, value: string | undefined) => {
    if (value === undefined) return;
    vars[`--${device}-${key}`] = value;
    classes.push(`ava-${device}-${key}`);
  };
  add('title', s.titleSize !== undefined ? `${s.titleSize}px` : undefined);
  add('body', s.bodySize !== undefined ? `${s.bodySize}px` : undefined);
  add('lh', s.lineHeight !== undefined ? String(s.lineHeight) : undefined);
  add('ls', s.letterSpacing !== undefined ? `${s.letterSpacing}px` : undefined);
  add('weight', s.titleWeight !== undefined ? String(s.titleWeight) : undefined);
  add('align', s.align);
  add('py', s.paddingY !== undefined ? `${s.paddingY}px` : undefined);
  add('px', s.paddingX !== undefined ? `${s.paddingX}px` : undefined);
  return {
    vars,
    className: classes.join(' ')
  };
}

/** Ajustes responsive (móvil + tablet) combinados de un bloque. */
function mobileStyle(block: LandingBlock): {
  vars: React.CSSProperties;
  className: string;
} {
  const mobile = deviceStyle(block, 'm');
  const tablet = deviceStyle(block, 't');
  return {
    vars: {
      ...mobile.vars,
      ...tablet.vars
    } as React.CSSProperties,
    className: `${mobile.className} ${tablet.className}`.trim()
  };
}

/** Traduce el estilo editable del bloque a CSS en línea. */
function wrapperStyle(block: LandingBlock): React.CSSProperties {
  const s = block.style;
  if (!s) return {};
  return {
    backgroundColor: s.background ? BACKGROUNDS[s.background] : undefined,
    color: s.textColor,
    paddingTop: s.paddingY !== undefined ? `${s.paddingY}px` : undefined,
    paddingBottom: s.paddingY !== undefined ? `${s.paddingY}px` : undefined,
    paddingLeft: s.paddingX !== undefined ? `${s.paddingX}px` : undefined,
    paddingRight: s.paddingX !== undefined ? `${s.paddingX}px` : undefined,
    borderRadius: s.radius !== undefined ? `${s.radius}px` : undefined,
    boxShadow: s.shadow ? SHADOWS[s.shadow] : undefined,
    textAlign: s.align,
    letterSpacing: s.letterSpacing !== undefined ? `${s.letterSpacing}px` : undefined,
    fontWeight: s.titleWeight,
    textTransform: s.uppercase ? 'uppercase' : undefined,
    overflow: s.radius ? 'hidden' : undefined
  };
}
const BUTTON_ANIMATION: Record<string, string> = {
  ninguna: '',
  elevar: 'hover:-translate-y-0.5',
  escalar: 'hover:scale-[1.03]',
  pulso: 'animate-pulse'
};
const BUTTON_ICONS: Record<string, React.ComponentType<{
  className?: string;
}>> = {
  ArrowRight: ArrowRightIcon,
  Sparkles: SparklesIcon,
  Send: SendIcon,
  Calendar: CalendarDaysIcon,
  MessageCircle: MessageCircleIcon,
  Phone: PhoneIcon
};

/** Botón de un bloque, totalmente personalizable desde el constructor. */
function BlockCta({
  block,
  label,
  onClick
}: {
  block: LandingBlock;
  label: string;
  onClick: () => void;
}) {
  const [hover, setHover] = React.useState(false);
  const b = block.button ?? {};
  const Icon = b.icon ? BUTTON_ICONS[b.icon] : undefined;
  const style: React.CSSProperties = {
    backgroundColor: hover && b.hoverColor ? b.hoverColor : b.color,
    color: b.textColor,
    borderRadius: b.radius !== undefined ? `${b.radius}px` : undefined,
    height: b.height !== undefined ? `${b.height}px` : undefined,
    paddingLeft: b.paddingX !== undefined ? `${b.paddingX}px` : undefined,
    paddingRight: b.paddingX !== undefined ? `${b.paddingX}px` : undefined,
    width: b.fullWidth ? '100%' : undefined
  };
  return <Button size="lg" onClick={onClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} style={style} className={`h-14 w-full rounded-xl px-8 transition-transform duration-200 sm:w-auto ${b.shadow === false ? '' : 'ava-glow'} ${BUTTON_ANIMATION[b.animation ?? 'elevar'] ?? ''}`} rightIcon={Icon ? <Icon className="h-4 w-4" data-id="element-396" data-mp="3icqvsvazi72d" /> : undefined} data-id="element-395" data-mp="34e0zhhvzobf2">
      {b.label || label}
    </Button>;
}

/** Resalta en azul uno o varios fragmentos, separados por "|". */
function Highlighted({
  text,
  highlight
}: {
  text: string;
  highlight: string;
}) {
  const segments = highlight.split('|').map(s => s.trim()).filter(Boolean);
  if (segments.length === 0) return <>{text}</>;
  const escaped = segments.map(s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const parts = text.split(new RegExp(`(${escaped.join('|')})`, 'g'));
  return <>
      {parts.map((part, index) => segments.includes(part) ? <span key={`${part}-${index}`} className="text-primary" data-id="element-397" data-mp="25zsoiuoym907">
            {part}
          </span> : <React.Fragment key={`t-${index}`} data-id="element-398" data-mp="212tuec5aq220">{part}</React.Fragment>)}
    </>;
}

/** Anotación manuscrita con flecha apuntando al vídeo. */
function HandwrittenNote({
  text
}: {
  text: string;
}) {
  return <div className="mt-6 flex items-start justify-center gap-2 sm:gap-3" data-id="element-399" data-mp="1s013ia00zscp">
      <svg width="48" height="34" viewBox="0 0 48 34" fill="none" aria-hidden="true" className="mt-1 flex-none text-primary" data-id="element-400" data-mp="lkqm8u0whr6r">
        <path d="M46 30C33 33 12 31 4 20C1.5 16.4 2.5 10 8 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" data-id="element-401" data-mp="gkvw4soy95mr" />
        <path d="M4 2.5L8.2 6.2L3.4 9.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" data-id="element-402" data-mp="2sb3r5p5162gb" />
      </svg>
      <p className="font-hand text-xl text-primary sm:text-2xl" data-id="element-403" data-mp="31a7b0vc0ulvv">{text}</p>
    </div>;
}
export function LandingRenderer({
  landing,
  form,
  onSubmit,
  preview = false,
  authLinks = false,
  selectedBlockId = null,
  onSelectBlock,
  viewport = 'desktop'
}: LandingRendererProps) {
  const config = landing.pageConfig;
  const theme = config.theme;
  const surfaceClass = theme === 'claro_corporativo' ? 'bg-surface' : 'bg-background';
  const cardShadow = theme === 'minimalista' ? '' : 'shadow-soft';
  const blocks = config.blocks.filter(b => b.visible);
  const rootRef = React.useRef<HTMLDivElement>(null);

  // Al seleccionar un bloque en el constructor, la vista previa se desplaza
  // suavemente hasta él para mantener panel y preview sincronizados.
  React.useEffect(() => {
    if (!selectedBlockId || !onSelectBlock) return;
    const target = rootRef.current?.querySelector(`[data-block-id="${selectedBlockId}"]`);
    target?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }, [selectedBlockId, onSelectBlock]);

  /** Logotipo de la empresa, con altura configurable por bloque. */
  function mark(height = 30) {
    return <BrandLogo src={config.logoUrl} size={height} alt={landing.name} data-id="element-404" data-mp="2vyuv7uc1ic2e" />;
  }
  const brandMark = mark();
  function renderBlock(block: LandingBlock) {
    switch (block.type) {
      case 'topbar':
        {
          const ctaLabel = cfg(block, 'ctaLabel', '');
          const centered = cfg(block, 'logoAlign', 'center') === 'center';
          const showLogo = flag(block, 'showLogo', true);
          const logoHeight = Number(cfg(block, 'logoHeight', 34)) || 34;
          // El acceso «Iniciar sesión» se puede ocultar desde el editor sin
          // dejar espacio vacío: si no hay nada que mostrar, no se renderiza.
          const showAuth = authLinks && flag(block, 'showAuth', true);
          const actions = <div className="flex items-center gap-2 sm:gap-4" data-id="element-405" data-mp="1cwj00bgtrn9u">
            {showAuth && <Link to="/login" className="whitespace-nowrap text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground sm:text-sm" data-id="element-406" data-mp="17yheljvxfbv7">
                Iniciar sesión
              </Link>}
            {ctaLabel && <Button size="sm" onClick={scrollToForm} className="whitespace-nowrap rounded-full px-4 text-[13px] sm:px-5 sm:text-sm" data-id="element-407" data-mp="y3za4ciorcac">
                {ctaLabel}
              </Button>}
          </div>;
          const hasActions = Boolean(ctaLabel) || showAuth;
          return <header key={block.id} className="sticky top-0 z-30 w-full border-b border-border/70 bg-background/80 backdrop-blur-xl" data-id="element-408" data-mp="1lxzaej7n3pe2">
            {centered ?
            // Logo centrado: las acciones se reservan su propio espacio a
            // izquierda y derecha, así nunca se solapan con el logotipo.
            <div className="mx-auto grid max-w-6xl grid-cols-[1fr_auto_1fr] items-center gap-2 px-4 py-3 sm:gap-3 sm:px-8 sm:py-4" data-id="element-409" data-mp="29ntkit0kqzum">
                <span aria-hidden="true" data-id="element-410" data-mp="cqp723y34grs" />
                <div className="flex min-w-0 justify-center" data-id="element-411" data-mp="cpdubemnvyoj">{showLogo && mark(logoHeight)}</div>
                <div className="flex justify-end" data-id="element-412" data-mp="3u65eganirfnn">{hasActions && actions}</div>
              </div> : <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:gap-4 sm:px-8 sm:py-4" data-id="element-413" data-mp="30411vg309yu">
                {showLogo ? mark(logoHeight) : <span data-id="element-414" data-mp="3l8v4csmftwkb" />}
                {hasActions && actions}
              </div>}
          </header>;
        }
      case 'logo':
        return <div key={block.id} className="flex justify-center px-5 pt-14" data-id="element-415" data-mp="3b949uqxvnhk8">
            {mark(Number(cfg(block, 'logoHeight', 34)) || 34)}
          </div>;
      case 'hero':
        {
          const title = cfg(block, 'title', '');
          const eyebrow = cfg(block, 'eyebrow', '');
          return <section key={block.id} className="px-6 pb-4 pt-10 text-center sm:px-8 sm:pb-6 sm:pt-24" data-id="element-416" data-mp="1egkkdzurbaqz">
            <div className="mx-auto w-full max-w-4xl" data-id="element-417" data-mp="2yxk1py6da347">
              {flag(block, 'showLogo', false) && <div className="mb-8 flex justify-center sm:mb-12" data-id="element-418" data-mp="2tz8nzccyb8gp">
                  {mark(Number(cfg(block, 'logoHeight', 34)) || 34)}
                </div>}
              {eyebrow && <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-primary sm:text-xs sm:tracking-[0.3em]" data-id="element-419" data-mp="38pzfejams5ja">
                  {eyebrow}
                </p>}
              {cfg(block, 'brandLine', '') && <p className="mx-auto mt-2.5 max-w-[19rem] text-[10px] font-bold uppercase leading-[1.5] tracking-[0.14em] text-navy sm:mt-4 sm:max-w-none sm:text-[13px] sm:tracking-[0.3em]" data-id="element-420" data-mp="3hqe3fqezbyqh">
                  {cfg(block, 'brandLine', '')}
                </p>}
              {/* Móvil: 40 px con interlineado 1.05 para que el titular no se
                  parta de forma poco elegante; escritorio sin cambios. */}
              <h1 className="mx-auto mt-5 max-w-3xl whitespace-pre-line text-[40px] font-extrabold leading-[1.05] tracking-[-0.03em] text-navy [word-break:keep-all] sm:mt-9 sm:text-[54px] sm:font-bold sm:leading-[1.08] sm:tracking-[-0.025em]" data-id="element-421" data-mp="1s9wn4hn5uao5">
                <Highlighted text={title} highlight={cfg(block, 'highlight', '')} data-id="element-422" data-mp="oa81szggckvt" />
              </h1>
              {cfg(block, 'subtitle', '') && <p className="mx-auto mt-5 max-w-xl text-[17px] leading-[1.55] text-muted-foreground sm:mt-8 sm:text-base sm:leading-relaxed" data-id="element-423" data-mp="t91if0r1cs07">
                  <Highlighted text={cfg(block, 'subtitle', '')} highlight={cfg(block, 'subtitleHighlight', '')} data-id="element-424" data-mp="mzzmqeblry0" />
                </p>}
              {cfg(block, 'extraLine', '') && <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground" data-id="element-425" data-mp="9mwtd6fkhgzx">
                  {cfg(block, 'extraLine', '')}
                </p>}
            </div>
          </section>;
        }
      case 'titulo':
        return <section key={block.id} className={`px-5 ${SPACING[block.spacing]}`} data-id="element-426" data-mp="3e1wkengt55lz">
            <h2 className="mx-auto max-w-3xl text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-427" data-mp="3i2gfzmlqg160">
              {cfg(block, 'text', '')}
            </h2>
          </section>;
      case 'subtitulo':
        return <section key={block.id} className="px-5 pb-8" data-id="element-428" data-mp="35julpe5x15h0">
            <p className="mx-auto max-w-2xl text-center text-lg text-muted-foreground" data-id="element-429" data-mp="35i9spsz91ul9">
              {cfg(block, 'text', '')}
            </p>
          </section>;
      case 'imagen':
        return <section key={block.id} className={`px-5 ${SPACING[block.spacing]}`} data-id="element-430" data-mp="2wlikyjbc4rau">
            {cfg(block, 'url', '') ? <img src={cfg(block, 'url', '')} alt={cfg(block, 'alt', '')} className={`mx-auto w-full max-w-5xl rounded-2xl object-cover ${cardShadow}`} data-id="element-431" data-mp="2rm6187frg0ch" /> : <div className="mx-auto flex aspect-video w-full max-w-5xl items-center justify-center rounded-2xl border border-dashed border-border bg-surface text-sm text-muted-foreground" data-id="element-432" data-mp="2htqktek43jtp">
                Añade una imagen desde el editor
              </div>}
          </section>;
      case 'video':
        return <section key={block.id} className="px-4 pb-4 pt-7 sm:px-8 sm:pb-8 sm:pt-10" data-id="element-433" data-mp="23vs4i3m8ddbk">
            <VideoReplay url={cfg(block, 'url', '')} thumbnailUrl={cfg(block, 'thumbnailUrl', '')} overlayTitle={cfg(block, 'overlayTitle', '') ? <Highlighted text={cfg(block, 'overlayTitle', '')} highlight={cfg(block, 'overlayHighlight', '')} data-id="element-435" data-mp="1z7spm2b9eyp4" /> : undefined} maxWidthClass="max-w-[760px]" editing={preview} data-id="element-434" data-mp="1u6vajyljqkru" />
            {cfg(block, 'note', '') && <HandwrittenNote text={cfg(block, 'note', '')} data-id="element-436" data-mp="1p8jxlezyfu5e" />}
            {cfg(block, 'caption', '') && <p className="mx-auto mt-6 max-w-2xl text-center text-base text-muted-foreground" data-id="element-437" data-mp="2cy4m0devnjm2">
                {cfg(block, 'caption', '')}
              </p>}
            {cfg(block, 'ctaLabel', '') && <div className="mx-auto mt-9 flex max-w-[380px] justify-center" data-id="element-438" data-mp="2371jr8a7oyl7">
                <BlockCta block={block} label={cfg(block, 'ctaLabel', '')} onClick={scrollToForm} data-id="element-439" data-mp="2864fu387ruh1" />
              </div>}
          </section>;
      case 'beneficios':
        {
          const items = cfg<{
            icon: string;
            title: string;
            text: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-440" data-mp="p6wzqcs05ltx">
            <div className="mx-auto max-w-6xl" data-id="element-441" data-mp="k8lrg6hcvjdt">
              {cfg(block, 'title', '') && <h2 className="mb-14 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-442" data-mp="ywzrk9ne0tqo">
                  {cfg(block, 'title', '')}
                </h2>}
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-443" data-mp="69429q05e43x">
                {items.map((item, index) => {
                  const Icon = ICONS[item.icon] ?? SparklesIcon;
                  return <article key={`${item.title}-${index}`} className={`group rounded-2xl border border-border bg-background p-8 text-center transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 ${cardShadow}`} data-id="element-444" data-mp="f9sf4hdt1yf8" data-mpi={index}>
                      <span className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 transition-colors group-hover:bg-primary/15" data-id="element-445" data-mp="1qo5zhc9cnvqi">
                        <Icon className="h-6 w-6 text-primary" data-id="element-446" data-mp="3g6kyqtrucehw" />
                      </span>
                      <h3 className="mb-3 text-base font-semibold text-navy" data-id="element-447" data-mp="3l5604witx47x">
                        {item.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-muted-foreground" data-id="element-448" data-mp="3q3qq834da7in">
                        {item.text}
                      </p>
                    </article>;
                })}
              </div>
            </div>
          </section>;
        }
      case 'pilares':
        {
          const items = cfg<{
            icon: string;
            title: string;
            text: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className="px-5 py-10 sm:px-8 sm:py-14" data-id="element-449" data-mp="cnrpuua0cdu2">
            <div className="mx-auto max-w-5xl rounded-3xl bg-surface px-6 py-12 sm:px-10 sm:py-14" data-id="element-450" data-mp="hnxiods0k6dq">
              <div className="grid gap-10 sm:grid-cols-3 sm:gap-0" data-id="element-451" data-mp="2eitmz95li7ft">
                {items.map((item, index) => {
                  const Icon = ICONS[item.icon] ?? SparklesIcon;
                  return <article key={`${item.title}-${index}`} className={`px-2 text-center sm:px-7 ${index > 0 ? 'sm:border-l sm:border-border/70' : ''}`} data-id="element-452" data-mp="10mijj9xy44p5" data-mpi={index}>
                      <Icon className="mx-auto mb-6 h-9 w-9 text-primary" aria-hidden="true" data-id="element-453" data-mp="rs46le0w1697" />
                      <h3 className="mb-4 text-lg font-bold text-navy" data-id="element-454" data-mp="mtsuewn4ejnt">
                        {item.title}
                      </h3>
                      <p className="mx-auto max-w-xs text-sm leading-relaxed text-muted-foreground" data-id="element-455" data-mp="1jiihd1dlu2gn">
                        {item.text}
                      </p>
                    </article>;
                })}
              </div>
            </div>
          </section>;
        }
      case 'frase':
        return <section key={block.id} className="px-5 py-12 sm:px-8 sm:py-16" data-id="element-456" data-mp="15j8zzydnx2b3">
            <div className="mx-auto flex max-w-4xl items-center gap-5 sm:gap-8" data-id="element-457" data-mp="3v9me1kcr0b93">
              <span className="hidden h-px flex-1 bg-border sm:block" aria-hidden="true" data-id="element-458" data-mp="3qaj71ud7juyh" />
              <p className="flex-1 whitespace-pre-line text-center text-[11px] font-bold uppercase leading-relaxed tracking-[0.16em] text-navy sm:flex-none sm:text-sm sm:tracking-[0.18em]" data-id="element-459" data-mp="5s0hvwoa7e2p">
                {cfg(block, 'text', '')}
              </p>
              <span className="hidden h-px flex-1 bg-border sm:block" aria-hidden="true" data-id="element-460" data-mp="3s8cy8vcxcpau" />
            </div>
          </section>;
      case 'estadisticas':
        {
          const items = cfg<{
            value: string;
            label: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 ${SPACING[block.spacing]}`} data-id="element-461" data-mp="jt33677gckck">
            <div className="mx-auto grid max-w-4xl gap-10 text-center sm:grid-cols-3" data-id="element-462" data-mp="ervwkbh1pr7h">
              {items.map(item => <div key={item.label} data-id="element-463" data-mp="9skbiq9nbzk3">
                  <p className="text-4xl font-semibold tracking-tight text-primary sm:text-5xl" data-id="element-464" data-mp="9vonwax9m1lu">
                    {item.value}
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground" data-id="element-465" data-mp="2ujjottl46vjy">{item.label}</p>
                </div>)}
            </div>
          </section>;
        }
      case 'testimonios':
        {
          const items = cfg<{
            name: string;
            role: string;
            company: string;
            text: string;
            avatar?: string;
            result?: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-466" data-mp="1os9g2bjxf9h1">
            <div className="mx-auto max-w-6xl" data-id="element-467" data-mp="1tp8aywbezkga">
              <h2 className="mb-14 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-468" data-mp="1xr3apwdp5pry">
                {cfg(block, 'title', 'Testimonios')}
              </h2>
              <div className="grid gap-6 md:grid-cols-3" data-id="element-469" data-mp="27pizvtt5yoyt">
                {items.map(item => <figure key={item.name} className={`flex flex-col rounded-2xl border border-border bg-background p-8 ${cardShadow}`} data-id="element-470" data-mp="27orcv7dmtzk5">
                    {item.result && <span className="mb-5 inline-flex w-fit items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary" data-id="element-471" data-mp="daq6m1fr6aa1">
                        {item.result}
                      </span>}
                    <blockquote className="flex-1 text-[15px] leading-relaxed text-foreground" data-id="element-472" data-mp="1a9ydvxalgdb">
                      “{item.text}”
                    </blockquote>
                    <figcaption className="mt-7 flex items-center gap-3 border-t border-border pt-6" data-id="element-473" data-mp="aa6h7gx03l0g">
                      {item.avatar ? <img src={item.avatar} alt="" className="h-11 w-11 rounded-full object-cover" data-id="element-474" data-mp="o8weoxnqfmpc" /> : <span className="flex h-11 w-11 items-center justify-center rounded-full bg-surface text-sm font-semibold text-muted-foreground" data-id="element-475" data-mp="k8ch5sz8yh2o">
                          {item.name.charAt(0)}
                        </span>}
                      <span className="min-w-0 flex-1 text-sm" data-id="element-476" data-mp="t1pkzpyun33j">
                        <span className="block truncate font-semibold text-foreground" data-id="element-477" data-mp="17xf8xtnlhqaw">
                          {item.name}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground" data-id="element-478" data-mp="130f39junqpkb">
                          {item.role}
                        </span>
                      </span>
                      <span className="hidden text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground sm:block" data-id="element-479" data-mp="1qlfphcmnngdv">
                        {item.company}
                      </span>
                    </figcaption>
                  </figure>)}
              </div>
            </div>
          </section>;
        }
      case 'logos_clientes':
        {
          const items = cfg<string[]>(block, 'items', []);
          return <section key={block.id} className="px-5 py-14" data-id="element-480" data-mp="17oy217eua2nf">
            <p className="mb-8 text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground" data-id="element-481" data-mp="twzyrd7t70z6">
              {cfg(block, 'title', '')}
            </p>
            <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-center gap-x-12 gap-y-6" data-id="element-482" data-mp="jx0tbp6orqqj">
              {items.map(name => <span key={name} className="text-lg font-semibold tracking-tight text-muted-foreground/70" data-id="element-483" data-mp="ovaow0wbotco">
                  {name}
                </span>)}
            </div>
          </section>;
        }
      case 'formulario':
        return <section key={block.id} id="formulario" className={`scroll-mt-24 px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-484" data-mp="1nasnhbfv75dl">
            <div className="mx-auto mb-10 max-w-2xl text-center sm:mb-12" data-id="element-485" data-mp="1diwnfuu7x56q">
              <h2 className="text-[26px] font-semibold leading-tight tracking-tight text-navy sm:text-4xl" data-id="element-486" data-mp="1dgirljrproy4">
                {cfg(block, 'title', 'Solicita información')}
              </h2>
              <p className="mt-4 text-[15px] leading-relaxed text-muted-foreground sm:text-lg" data-id="element-487" data-mp="14gnh7h5zfxz5">
                {cfg(block, 'subtitle', '')}
              </p>
            </div>
            {form ? <DynamicForm form={form} onSubmit={onSubmit} preview={preview} showProgress={cfg(block, 'showProgress', 'si') !== 'no'} hidePrivacyNotice={cfg(block, 'showPrivacy', 'si') === 'no'} data-id="element-488" data-mp="ulbxekfpw0kj" /> : <div className="mx-auto max-w-2xl rounded-2xl border border-dashed border-border bg-surface p-12 text-center text-sm text-muted-foreground" data-id="element-489" data-mp="2oytm4aruz3h5">
                Asocia un formulario a esta landing desde el editor.
              </div>}
          </section>;
      case 'faq':
        {
          const items = cfg<{
            q: string;
            a: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-490" data-mp="3b5o0k3gsvbgr">
            <div className="mx-auto max-w-3xl" data-id="element-491" data-mp="2djqoant0k2d9">
              <h2 className="mb-12 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-492" data-mp="2ihjsxceo4obf">
                {cfg(block, 'title', 'Preguntas frecuentes')}
              </h2>
              <div className="space-y-3" data-id="element-493" data-mp="2nized5krcwuc">
                {items.map(item => <details key={item.q} className="group rounded-xl border border-border bg-background px-5 py-4 transition-colors hover:border-primary/30" data-id="element-494" data-mp="2sghce0x8lrnk">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-[15px] font-medium text-foreground marker:hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" data-id="element-495" data-mp="7p8iu1qj02v7">
                      {item.q}
                      <ChevronDownIcon className="h-4 w-4 flex-none text-muted-foreground transition-transform duration-200 group-open:rotate-180" aria-hidden="true" data-id="element-496" data-mp="1ytahavaxk3dm" />
                    </summary>
                    <p className="mt-4 text-sm leading-relaxed text-muted-foreground" data-id="element-497" data-mp="28shpz56pz9ue">
                      {item.a}
                    </p>
                  </details>)}
              </div>
            </div>
          </section>;
        }
      case 'calendario':
        {
          // La reserva usa el calendario global de la empresa salvo que la
          // landing tenga uno propio. La lógica de reserva no cambia.
          const custom = cfg(block, 'calendarSource', 'global') === 'custom';
          const bookingUrl = String((custom ? cfg(block, 'url', '') : cfg(block, 'globalUrl', '') || cfg(block, 'url', '')) as string).trim();
          // Sin enlace válido el bloque no se muestra en la landing pública:
          // así nunca queda un hueco ni se carga ningún script de calendario.
          if (!bookingUrl && !preview) return null;
          const embedded = cfg(block, 'calendarMode', 'boton') === 'incrustado' && Boolean(bookingUrl);
          const embedHeight = Number(cfg(block, 'embedHeight', 700)) || 700;
          if (embedded) {
            return <section key={block.id} id="calendario" className={`scroll-mt-24 px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-498" data-mp="15yucdf1m176e">
              <div className="mx-auto w-full max-w-3xl text-center" data-id="element-499" data-mp="1axowckbbd1s3">
                <h2 className="text-2xl font-bold tracking-tight text-navy sm:text-3xl" data-id="element-500" data-mp="w1pqlgbofdq4">
                  {cfg(block, 'title', 'Agenda una llamada')}
                </h2>
                <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base" data-id="element-501" data-mp="3gr5eo3jigoc2">
                  {cfg(block, 'text', '')}
                </p>
                <div className="mt-8 overflow-hidden rounded-3xl border border-border bg-background shadow-soft" data-id="element-502" data-mp="3lo5j6a0ppj12">
                  <iframe src={bookingUrl} title={cfg(block, 'title', 'Calendario de reservas')} className="w-full" style={{
                    height: `${embedHeight}px`
                  }} loading="lazy" data-id="element-503" data-mp="32xcvr9i3doid" />
                </div>
              </div>
            </section>;
          }
          return <section key={block.id} id="calendario" className={`scroll-mt-24 px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-504" data-mp="2p21275foa431">
            <div className="mx-auto w-full max-w-3xl rounded-3xl border border-border bg-background p-7 text-center shadow-soft sm:p-12" data-id="element-505" data-mp="3gr41s7wm6q9c">
              <CalendarDaysIcon className="mx-auto mb-5 h-8 w-8 text-primary" aria-hidden="true" data-id="element-506" data-mp="3bzcdk40xkwmd" />
              <h2 className="text-2xl font-bold tracking-tight text-navy sm:text-3xl" data-id="element-507" data-mp="37xr5cvzcd6ii">
                {cfg(block, 'title', 'Agenda una llamada')}
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base" data-id="element-508" data-mp="3kuakgpuk79m">
                {cfg(block, 'text', '')}
              </p>
              <div className="mt-9 flex justify-center" data-id="element-509" data-mp="1m9f0tn3betyc">
                <Button size="lg" className="h-14 w-full rounded-xl px-8 sm:w-auto" onClick={() => {
                  if (bookingUrl) window.open(bookingUrl, '_blank', 'noopener');
                }} data-id="element-510" data-mp="1r7ghy2h6kj76">
                  {cfg(block, 'ctaLabel', 'Reservar hora')}
                </Button>
              </div>
              {!bookingUrl && <p className="mt-5 text-xs text-muted-foreground" data-id="element-511" data-mp="255ojknp5w8oo">
                  Conecta Google Calendar en Integraciones o añade una URL de
                  reserva a este bloque.
                </p>}
            </div>
          </section>;
        }
      case 'whatsapp':
        return <a key={block.id} href={`https://wa.me/${String(cfg(block, 'number', '')).replace(/\D/g, '')}?text=${encodeURIComponent(cfg(block, 'message', ''))}`} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg transition-transform hover:scale-105" aria-label="Contactar por WhatsApp" data-id="element-512" data-mp="2iwjmljczbvkc">
            <MessageCircleIcon className="h-6 w-6" data-id="element-513" data-mp="21evbgi97vtf" />
          </a>;
      case 'cta_final':
        {
          // Con número de WhatsApp el botón abre el chat; si no, baja al formulario.
          const waNumber = String(cfg(block, 'whatsappNumber', '')).replace(/\D/g, '');
          return <section key={block.id} className={`px-5 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-514" data-mp="gp9t3bb4cqdd">
            <div className="mx-auto max-w-3xl text-center" data-id="element-515" data-mp="krd45ytvlsy4">
              <h2 className="text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-516" data-mp="ksy1fw7krpzo">
                {cfg(block, 'title', '')}
              </h2>
              <p className="mt-4 text-lg text-muted-foreground" data-id="element-517" data-mp="bxi9xlualmx9">
                {cfg(block, 'text', '')}
              </p>
              <div className="mx-auto mt-10 flex max-w-[380px] justify-center" data-id="element-518" data-mp="zn2ug963gs8n">
                <BlockCta block={block} label={cfg(block, 'ctaLabel', 'Quiero empezar')} onClick={() => {
                  if (waNumber) {
                    window.open(`https://wa.me/${waNumber}?text=${encodeURIComponent(cfg(block, 'whatsappMessage', ''))}`, '_blank', 'noopener');
                    return;
                  }
                  scrollToForm();
                }} data-id="element-519" data-mp="14fvvalyfzgfg" />
              </div>
            </div>
          </section>;
        }
      case 'footer':
        {
          // Solo se muestran las redes con URL configurada.
          const socials = cfg<{
            network: string;
            url: string;
          }[]>(block, 'socials', []).filter(s => s.url?.trim());
          return <footer key={block.id} className="border-t border-border px-5 py-12 sm:px-8" data-id="element-520" data-mp="14usrw8kar1nv">
            <div className="mx-auto flex max-w-6xl flex-col items-center gap-8 sm:flex-row sm:items-center sm:justify-between" data-id="element-521" data-mp="ztd96n5ld78n">
              <div className="flex flex-col items-center gap-4 sm:items-start" data-id="element-522" data-mp="2gahf911gq2p7">
                {brandMark}
              </div>
              <div className="flex flex-col items-center gap-2 text-center text-xs text-muted-foreground" data-id="element-523" data-mp="2ja9hmz4oo2k4">
                <p data-id="element-524" data-mp="2nb1s1vpakdhz">{cfg(block, 'text', '')}</p>
                <div className="flex flex-wrap justify-center gap-x-5 gap-y-1" data-id="element-525" data-mp="2n9rquxn56p74">
                  {cfg<string[]>(block, 'links', []).map(link => <span key={link} className="cursor-pointer hover:text-foreground" data-id="element-526" data-mp="2s8utycjkalcg">
                      {link}
                    </span>)}
                </div>
              </div>
              {socials.length > 0 && <div className="flex gap-3 text-muted-foreground" data-id="element-527" data-mp="24l3bssbcbe39">
                  {socials.map((social, index) => {
                  const network = SOCIAL_NETWORKS[social.network];
                  const Icon = network?.icon ?? LinkIcon;
                  return <a key={`${social.network}-${index}`} href={social.url} target="_blank" rel="noopener noreferrer" aria-label={network?.label ?? social.network} className="flex h-9 w-9 items-center justify-center rounded-full border border-border transition-colors hover:border-primary/40 hover:text-primary" data-id="element-528" data-mp="2eboeory1r4rz" data-mpi={index}>
                        <Icon className="h-4 w-4" aria-hidden="true" data-id="element-529" data-mp="1eab7zgmvl9ns" />
                      </a>;
                })}
                </div>}
            </div>
          </footer>;
        }
      case 'servicios':
        {
          const items = cfg<{
            icon: string;
            title: string;
            text: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-530" data-mp="vda2lupet7jb">
            <div className="mx-auto max-w-6xl" data-id="element-531" data-mp="ql8i5li38ai7">
              {cfg(block, 'title', '') && <h2 className="text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-532" data-mp="lm5osybgzsq2">
                  {cfg(block, 'title', '')}
                </h2>}
              {cfg(block, 'subtitle', '') && <p className="mx-auto mt-4 max-w-2xl text-center text-base text-muted-foreground" data-id="element-533" data-mp="bnzogl5baunb">
                  {cfg(block, 'subtitle', '')}
                </p>}
              <div className={`grid gap-6 md:grid-cols-3 ${cfg(block, 'title', '') || cfg(block, 'subtitle', '') ? 'mt-14' : ''}`} data-id="element-534" data-mp="7nfozax9ney0">
                {items.map((item, index) => {
                  const Icon = ICONS[item.icon] ?? BriefcaseIcon;
                  return <article key={`${item.title}-${index}`} className={`rounded-2xl border border-border bg-background p-8 transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 ${cardShadow}`} data-id="element-535" data-mp="nlrwtiidv0ct" data-mpi={index}>
                      <span className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10" data-id="element-536" data-mp="dlr6vdq0dlzn">
                        <Icon className="h-5 w-5 text-primary" data-id="element-537" data-mp="ingml6o6enu2" />
                      </span>
                      <h3 className="mb-2 text-base font-semibold text-navy" data-id="element-538" data-mp="4nfizjgj12aw">
                        {item.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-muted-foreground" data-id="element-539" data-mp="1z05g0a26yrmi">
                        {item.text}
                      </p>
                    </article>;
                })}
              </div>
            </div>
          </section>;
        }
      case 'caracteristicas':
        {
          const items = cfg<{
            title: string;
            text: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-540" data-mp="14q9h1jzalbo">
            <div className="mx-auto max-w-4xl" data-id="element-541" data-mp="3sim7la4j2rgi">
              <h2 className="mb-12 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-542" data-mp="3sgrlvtlfmvbd">
                {cfg(block, 'title', 'Características')}
              </h2>
              <ul className="grid gap-x-10 gap-y-6 sm:grid-cols-2" data-id="element-543" data-mp="62ronbeaa0hv">
                {items.map((item, index) => <li key={`${item.title}-${index}`} className="flex gap-3" data-id="element-544" data-mp="k2j0p6l8p6t8" data-mpi={index}>
                    <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary/10" data-id="element-545" data-mp="f511w90xs7yy">
                      <CheckIcon className="h-3.5 w-3.5 text-primary" data-id="element-546" data-mp="2lunzn215p4dg" />
                    </span>
                    <span data-id="element-547" data-mp="2gwvzxlu9sdyp">
                      <span className="block text-[15px] font-medium text-foreground" data-id="element-548" data-mp="2gxp3yf1ugpd5">
                        {item.title}
                      </span>
                      <span className="block text-sm leading-relaxed text-muted-foreground" data-id="element-549" data-mp="ps2f8825rs2o">
                        {item.text}
                      </span>
                    </span>
                  </li>)}
              </ul>
            </div>
          </section>;
        }
      case 'galeria':
        {
          const items = cfg<string[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-550" data-mp="18plrdkjwxqtb">
            <div className="mx-auto max-w-6xl" data-id="element-551" data-mp="1dh3uae5hy1gx">
              {cfg(block, 'title', '') && <h2 className="mb-12 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-552" data-mp="1dj7v2n83mkcz">
                  {cfg(block, 'title', '')}
                </h2>}
              {items.length === 0 ? <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border bg-surface py-16 text-sm text-muted-foreground" data-id="element-553" data-mp="1mfgxmardhz3c">
                  <ImageIcon className="h-6 w-6" aria-hidden="true" data-id="element-554" data-mp="3bzgqnfoctwrj" />
                  Añade imágenes desde el editor
                </div> : <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-555" data-mp="36y9l7n9op9o1">
                  {items.map((url, index) => <img key={`${url}-${index}`} src={url} alt="" className={`aspect-[4/3] w-full rounded-2xl object-cover ${cardShadow}`} data-id="element-556" data-mp="3303fmikl104p" data-mpi={index} />)}
                </div>}
            </div>
          </section>;
        }
      case 'precios':
        {
          const items = cfg<{
            name: string;
            price: string;
            period: string;
            features: string;
            featured: string;
            ctaLabel: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-557" data-mp="3gx8riz6z8t7v">
            <div className="mx-auto max-w-5xl" data-id="element-558" data-mp="i92xe6uci8lf">
              <h2 className="text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-559" data-mp="dgi7vhih2hnp">
                {cfg(block, 'title', 'Planes')}
              </h2>
              {cfg(block, 'subtitle', '') && <p className="mx-auto mt-4 max-w-2xl text-center text-base text-muted-foreground" data-id="element-560" data-mp="3mq35jpvu6lic">
                  {cfg(block, 'subtitle', '')}
                </p>}
              <div className="mt-14 grid gap-6 md:grid-cols-2" data-id="element-561" data-mp="37yu6yr9yhpt2">
                {items.map((plan, index) => {
                  const featured = Boolean(plan.featured);
                  return <article key={`${plan.name}-${index}`} className={`flex flex-col rounded-2xl border bg-background p-8 ${featured ? 'border-primary shadow-soft' : 'border-border'}`} data-id="element-562" data-mp="4krhbvj5m7ya" data-mpi={index}>
                      {featured && <span className="mb-5 inline-flex w-fit rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary" data-id="element-563" data-mp="3roeezzi7yu01">
                          Recomendado
                        </span>}
                      <h3 className="text-base font-semibold text-navy" data-id="element-564" data-mp="2g87odqfz4eha">{plan.name}</h3>
                      <p className="mt-4 flex items-baseline gap-2" data-id="element-565" data-mp="2kcnnn8v30nnj">
                        <span className="text-4xl font-semibold tracking-tight text-navy" data-id="element-566" data-mp="2cqs7hi7iedhb">
                          {plan.price}
                        </span>
                        <span className="text-sm text-muted-foreground" data-id="element-567" data-mp="2mpr8qfpy36ua">
                          {plan.period}
                        </span>
                      </p>
                      <ul className="mt-7 flex-1 space-y-3" data-id="element-568" data-mp="2hrfyw4zngwc2">
                        {String(plan.features ?? '').split('\n').filter(Boolean).map(feature => <li key={feature} className="flex gap-2.5 text-sm text-muted-foreground" data-id="element-569" data-mp="1p20qlmzddhju">
                              <CheckIcon className="mt-0.5 h-4 w-4 flex-none text-primary" data-id="element-570" data-mp="23qn21dlvlp5s" />
                              {feature}
                            </li>)}
                      </ul>
                      <Button className="mt-8 w-full" variant={featured ? 'primary' : 'secondary'} onClick={scrollToForm} data-id="element-571" data-mp="1bam4v8e2v293">
                        {plan.ctaLabel || 'Empezar'}
                      </Button>
                    </article>;
                })}
              </div>
            </div>
          </section>;
        }
      case 'contacto':
        return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-572" data-mp="2ke9bryovy7s">
            <div className="mx-auto max-w-3xl rounded-2xl border border-border bg-background p-10 text-center shadow-soft" data-id="element-573" data-mp="glgrvuqh6qdy">
              <h2 className="text-2xl font-semibold tracking-tight text-navy sm:text-3xl" data-id="element-574" data-mp="bmnee2b6uzds">
                {cfg(block, 'title', 'Contacto')}
              </h2>
              <p className="mt-3 text-sm text-muted-foreground" data-id="element-575" data-mp="3f3cnnw25on75">
                {cfg(block, 'text', '')}
              </p>
              <dl className="mt-8 grid gap-4 sm:grid-cols-3" data-id="element-576" data-mp="3tsilpgp9c7dg">
                {[{
                icon: AtSignIcon,
                value: cfg(block, 'email', '')
              }, {
                icon: PhoneIcon,
                value: cfg(block, 'phone', '')
              }, {
                icon: MapPinIcon,
                value: cfg(block, 'address', '')
              }].filter(entry => entry.value).map(entry => <div key={entry.value} className="flex flex-col items-center gap-2 rounded-xl bg-surface px-4 py-5" data-id="element-577" data-mp="1btiuvds4skug">
                      <entry.icon className="h-5 w-5 text-primary" aria-hidden="true" data-id="element-578" data-mp="1gn3sv5tq5etn" />
                      <span className="break-words text-sm text-foreground" data-id="element-579" data-mp="12vgw74vve4ac">
                        {entry.value}
                      </span>
                    </div>)}
              </dl>
            </div>
          </section>;
      default:
        return null;
    }
  }

  /** En el constructor cada bloque es seleccionable haciendo clic sobre él. */
  function renderEditable(block: LandingBlock) {
    const content = renderBlock(block);
    const mobile = mobileStyle(block);
    const style = {
      ...wrapperStyle(block),
      ...mobile.vars
    };
    const hasStyle = Object.values(style).some(v => v !== undefined);
    if (!onSelectBlock) {
      return hasStyle || mobile.className ? <div key={block.id} style={style} className={mobile.className} data-id="element-580" data-mp="f4t9vel7jc49">
          {content}
        </div> : content;
    }
    const selected = selectedBlockId === block.id;
    return <div key={block.id} role="button" tabIndex={0} onClick={() => onSelectBlock(block.id)} onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelectBlock(block.id);
      }
    }} data-block-id={block.id} style={style} className={`relative cursor-pointer outline-none transition-shadow ${mobile.className} ${selected ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : 'hover:ring-2 hover:ring-primary/30'}`} data-id="element-581" data-mp="2btuokf2q7jea">
        {selected && <span className="pointer-events-none absolute left-3 top-3 z-40 rounded-full bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground" data-id="element-582" data-mp="1o40chzq8dyv3">
            {blockLabel(block.type)}
          </span>}
        {content}
      </div>;
  }
  return <div ref={rootRef} className={`w-full bg-background ${viewport === 'mobile' ? 'ava-viewport-mobile' : viewport === 'tablet' ? 'ava-viewport-tablet' : ''}`} style={{
    '--primary': config.primaryColor,
    fontFamily: `${config.fontFamily}, Inter, sans-serif`
  } as React.CSSProperties} data-id="element-583" data-mp="3ihrrlerbxaxx">
      {blocks.map(renderEditable)}
      {blocks.length === 0 && <div className="flex min-h-[300px] items-center justify-center p-10 text-center text-sm text-muted-foreground" data-id="element-584" data-mp="19yrokgfm8iwl">
          Esta landing no tiene bloques visibles. Activa alguno desde el editor.
        </div>}
    </div>;
}